import { useCallback, useEffect, useRef } from "react";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import {
  useHideLiveCallMutation,
  useUnhideLiveCallMutation
} from "services/dialstack";

import {
  livePrivacyDropped,
  livePrivacyFailed,
  livePrivacyRequested,
  livePrivacySet,
  selectLivePrivacy
} from "../hidden-calls.slice";

// A placed call's row may not exist until the call.initiated webhook lands;
// one retry covers the gap before the toggle gives up.
export const LIVE_PRIVACY_RETRY_DELAY_MS = 2000;

const isNotFound = (error: unknown) =>
  typeof error === "object" &&
  error !== null &&
  "status" in error &&
  (error as { status: unknown }).status === 404;

// The in-call "Private" toggle (ENG-2364): marks the call the user is on as
// hidden from the practice, by the SDK call id — the backend resolves the row
// (by provider id, else the user's live call), so neither direction needs the
// backend id in the widget. Reversible until the call ends.
export default function useLiveCallPrivacy(sdkCallId: string) {
  const dispatch = useAppDispatch();
  const privacy = useAppSelector((state) =>
    selectLivePrivacy(state, sdkCallId)
  );
  const [hideLiveCall] = useHideLiveCallMutation();
  const [unhideLiveCall] = useUnhideLiveCallMutation();
  const timer = useRef<number | null>(null);

  const isPrivate = privacy?.status === "on";
  const isPending = privacy?.status === "pending";

  const apply = useCallback(
    async (on: boolean, retried: boolean) => {
      const request = on ? hideLiveCall : unhideLiveCall;
      try {
        const call = await request({ dialstack_call_id: sdkCallId }).unwrap();
        dispatch(livePrivacySet({ sdkCallId, backendCallId: call.id, on }));
      } catch (error) {
        if (isNotFound(error) && !retried) {
          timer.current = window.setTimeout(() => {
            timer.current = null;
            apply(on, true);
          }, LIVE_PRIVACY_RETRY_DELAY_MS);
          return;
        }
        dispatch(livePrivacyFailed({ sdkCallId }));
        dispatch(
          setCustomMessage(
            on
              ? "Couldn't mark this call private"
              : "Couldn't make this call visible again"
          )
        );
        dispatch(setOpen(true));
      }
    },
    [dispatch, hideLiveCall, sdkCallId, unhideLiveCall]
  );

  const toggle = useCallback(() => {
    if (isPending) return;
    dispatch(livePrivacyRequested({ sdkCallId }));
    apply(!isPrivate, false);
  }, [apply, dispatch, isPending, isPrivate, sdkCallId]);

  // A retry still waiting is abandoned with the control; the entry itself
  // outlives it — the control unmounts whenever the panel folds to its bar,
  // which still has to say "Private call". `useLivePrivacyCleanup` drops it
  // once the call is gone.
  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    []
  );

  return { isPrivate, isPending, toggle };
}

// Forgets the toggle state of calls the SDK no longer lists (they ended).
// Mounted once by the widget with every live SDK call id.
export function useLivePrivacyCleanup(liveSdkCallIds: string[]) {
  const dispatch = useAppDispatch();
  const tracked = useAppSelector((state) => state.hiddenCalls.live);
  const liveKey = liveSdkCallIds.join("\u0000");
  useEffect(() => {
    const live = new Set(liveKey === "" ? [] : liveKey.split("\u0000"));
    Object.keys(tracked).forEach((sdkCallId) => {
      if (!live.has(sdkCallId)) dispatch(livePrivacyDropped({ sdkCallId }));
    });
  }, [dispatch, liveKey, tracked]);
}
